import React, { useEffect, useRef, useState } from "react";
import { submitSparql } from "../api/sparql";
import * as d3 from "d3";

const WIDTH = window.innerWidth * 0.8;
const HEIGHT = window.innerHeight * 0.8;
const truncate = (str, n = 30) => {
    return str.length > n ? str.slice(0, n - 1) + "..." : str;
};
export default function OntologyVisualizer() {
    const svgRef = useRef();
    const [graph, setGraph] = useState({ nodes: [], links: [] });
    const [expanded, setExpanded] = useState(new Set());
    const [menu, setMenu] = useState(null);
    const [rootClass, setRootClass] = useState("Component");

    useEffect(() => {
        initGraph();
    }, [rootClass]);
    useEffect(() => {
        renderGraph();
    }, [graph]);

    // --- 1. DATA FETCHING ---
    const initGraph = async () => {
        const query = `
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
PREFIX owl: <http://www.w3.org/2002/07/owl#>

SELECT ?root ?child (SAMPLE(?label) AS ?childLabel) ?hasChildren
WHERE {
  GRAPH stardog:context:schema {
    ?root rdfs:label "${rootClass}"@en .
    
    ?child rdfs:subClassOf+ ?root .
    FILTER (?child != ?root)

    ?child rdfs:label ?label

    BIND(EXISTS {
      ?anyChild rdfs:subClassOf ?child .
      FILTER(?anyChild != ?child && ?anyChild != owl:Nothing)
    } AS ?hasChildren)
  }
} 
GROUP BY ?root ?child ?hasChildren
LIMIT 100
        `;

        const [res, ok] = await submitSparql(query);
        if (!ok) return;
        setExpanded(new Set());
        setGraph(transform(res));
    };

    const expandNode = async (node) => {
        if (expanded.has(node.id)) return;
        setExpanded((prev) => new Set(prev).add(node.id));

        const query = `
        
        PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
        SELECT * WHERE {
          GRAPH stardog:context:schema {
            ?child rdfs:subClassOf <${node.id}> .
            FILTER (?child != <${node.id}>)
            FILTER NOT EXISTS {
              ?child rdfs:subClassOf ?mid . ?mid rdfs:subClassOf <${node.id}> .
              FILTER (?mid != <${node.id}> && ?mid != ?child)
            }
            ?child rdfs:label ?label
            BIND(EXISTS { ?gc rdfs:subClassOf ?child . FILTER (?gc != ?child) } AS ?hasChildren)
          }
        } LIMIT 100`;

        const [res, ok] = await submitSparql(query);
        if (!ok) return;

        const incoming = transformExpansion(node, res);

        setGraph((prev) => ({
            nodes: [
                ...prev.nodes,
                ...incoming.nodes.filter(
                    (n) => !prev.nodes.find((ex) => ex.id === n.id),
                ),
            ],
            links: [...prev.links, ...incoming.links],
        }));
    };

    // --- 2. DATA TRANSFORMERS ---
    const transform = (data) => {
        const nodesMap = new Map();
        const links = [];
        const bindings = data.results.bindings;
        if (bindings.length > 0) {
            const rootId = bindings[0].root.value;
            nodesMap.set(rootId, {
                id: rootId,
                label: rootClass,
                root: true,
                x: WIDTH / 2,
                y: HEIGHT / 2,
            });
            bindings.forEach((row) => {
                const childId = row.child.value;
                nodesMap.set(childId, {
                    id: childId,
                    label: row.childLabel?.value || childId.split("/").pop(),
                    hasChildren: row.hasChildren?.value === "true",
                });
                links.push({ source: rootId, target: childId });
            });
        }
        debugger;
        const res = { nodes: Array.from(nodesMap.values()), links };
        return res;
    };

    const transformExpansion = (parentNode, data) => {
        const nodes = [];
        const links = [];
        data.results.bindings.forEach((row) => {
            const id = row.child.value;
            nodes.push({
                id,
                label: row.label?.value || id.split("/").pop(),
                hasChildren: row.hasChildren?.value === "true",
                x: parentNode.x, // Spawn on parent for smooth exit
                y: parentNode.y,
            });
            links.push({ source: parentNode.id, target: id });
        });
        return { nodes, links };
    };

    // --- 3. D3 RENDERING (STATIC DRAG FOCUS) ---
    const renderGraph = () => {
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
        const container = svg.append("g");

        svg.call(
            d3
                .zoom()
                .scaleExtent([0.1, 3])
                .on("zoom", (e) => container.attr("transform", e.transform)),
        );

        debugger;
        const simulation = d3
            .forceSimulation(graph.nodes)
            .force(
                "link",
                d3
                    .forceLink(graph.links)
                    .id((d) => d.id)
                    .distance(140),
            )
            .force("charge", d3.forceManyBody().strength(-500))
            .force("center", d3.forceCenter(WIDTH / 2, HEIGHT / 2))
            .force("collision", d3.forceCollide().radius(50))
            .velocityDecay(0.7);

        const link = container
            .append("g")
            .selectAll("line")
            .data(graph.links)
            .enter()
            .append("line")
            .attr("stroke", "#cbd5e1")
            .attr("stroke-width", 2)
            .attr("stroke-opacity", 0.5);

        const node = container
            .selectAll("g.node")
            .data(graph.nodes)
            .enter()
            .append("g")
            .attr("class", "node")
            .style("cursor", "grab")
            .on("click", (e, d) => {
                e.stopPropagation();
                setMenu({ x: e.clientX, y: e.clientY, node: d });
            })
            .call(
                d3
                    .drag()
                    .on("start", function () {
                        d3.select(this).style("cursor", "grabbing");
                    })
                    .on("drag", function (event, d) {
                        // Update the data positions
                        d.x = event.x;
                        d.y = event.y;
                        // Update the visual position of this node immediately
                        d3.select(this).attr(
                            "transform",
                            `translate(${d.x},${d.y})`,
                        );
                        // Update ONLY the links connected to this node
                        link.filter(
                            (l) => l.source.id === d.id || l.target.id === d.id,
                        )
                            .attr("x1", (l) => l.source.x)
                            .attr("y1", (l) => l.source.y)
                            .attr("x2", (l) => l.target.x)
                            .attr("y2", (l) => l.target.y);
                    })
                    .on("end", function () {
                        d3.select(this).style("cursor", "grab");
                    }),
            );

        node.append("circle")
            .attr("r", (d) => (d.root ? 18 : 12))
            .attr("fill", (d) =>
                d.root ? "#f97316" : d.hasChildren ? "#8b5cf6" : "#14b8a6",
            )
            .attr("stroke", "#fff")
            .attr("stroke-width", 2);

        node.append("text")
            .text((d) => truncate(d.label))
            .attr("dy", 30)
            .attr("text-anchor", "middle")
            .style("font-size", "12px")
            .style("fill", "#334155")
            .style("font-weight", "600")
            .attr("stroke", "white")
            .attr("stroke-width", 3)
            .attr("paint-order", "stroke");

        // The 'tick' function handles the initial spread
        simulation.on("tick", () => {
            link.attr("x1", (d) => d.source.x)
                .attr("y1", (d) => d.source.y)
                .attr("x2", (d) => d.target.x)
                .attr("y2", (d) => d.target.y);
            node.attr("transform", (d) => `translate(${d.x},${d.y})`);
        });
    };

    return (
        <div
            style={{
                padding: "20px",
                backgroundColor: "#f8fafc",
                minHeight: "100vh",
                fontFamily: "sans-serif",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                }}
            >
                <h2 style={{ margin: 0, color: "#0f172a" }}>
                    Ontology Explorer
                </h2>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    <label style={{ fontWeight: "600", color: "#64748b" }}>
                        Select Category:
                    </label>
                    <select
                        value={rootClass}
                        onChange={(e) => setRootClass(e.target.value)}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            fontWeight: "bold",
                            color: "#4f46e5",
                        }}
                    >
                        <option value="Form">Form</option>
                        <option value="Substance">Substance</option>
                        <option value="Component">Component</option>
                    </select>
                </div>
            </div>

            <div
                onClick={() => setMenu(null)}
                style={{
                    position: "relative",
                    background: "white",
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                }}
            >
                <svg ref={svgRef} width={WIDTH} height={HEIGHT} />

                {menu && (
                    <div
                        style={{
                            position: "fixed",
                            top: menu.y,
                            left: menu.x + 15,
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                            zIndex: 100,
                            minWidth: "200px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ fontWeight: "700", marginBottom: "8px" }}>
                            {menu.node.label}
                        </div>
                        {menu.node.hasChildren &&
                            !expanded.has(menu.node.id) && (
                                <button
                                    onClick={() => {
                                        expandNode(menu.node);
                                        setMenu(null);
                                    }}
                                    style={{
                                        width: "100%",
                                        padding: "10px",
                                        background: "#4f46e5",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                    }}
                                >
                                    Expand Subclasses
                                </button>
                            )}
                        {!menu.node.hasChildren && (
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                                End of branch
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
